import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { waitStatus } from "@/components/MeetingRecorder";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bar, BarChart, CartesianGrid, Cell, Legend, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AlertTriangle, GraduationCap, Loader2, Sparkles, TrendingDown, Wand2 } from "lucide-react";

const fmt = (v) => (v == null ? "—" : Number(v).toFixed(1).replace(".", ","));
const tone = (v) => (v == null ? "#94a3b8" : v < 5 ? "#dc2626" : v < 7 ? "#f59e0b" : "#16a34a");
const TIP = { borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 };
const SHORT = { Linguagens: "Ling.", "Matemática": "Mat.", "Ciências da Natureza": "Nat.", "Ciências Humanas": "Hum.", Outras: "Outras" };
const useNarrow = () => {
  const [n, setN] = useState(typeof window !== "undefined" && window.innerWidth < 640);
  useEffect(() => { const f = () => setN(window.innerWidth < 640); window.addEventListener("resize", f); return () => window.removeEventListener("resize", f); }, []);
  return n;
};

const ChartCard = ({ title, icon: I, children, testid, right }) => (
  <Card className="border-slate-200 p-4" data-testid={testid}>
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <h3 className="flex items-center gap-2 font-display text-base font-semibold text-slate-800"><I className="h-5 w-5 text-blue-700" />{title}</h3>
      {right}
    </div>
    {children}
  </Card>
);

const Empty = ({ text = "Sem notas lançadas para este trimestre." }) => <p className="py-10 text-center text-sm text-slate-400">{text}</p>;

const AreaChartCard = ({ d }) => {
  const narrow = useNarrow();
  return (
  <ChartCard title={`Desempenho por área — Turma ${d.class_name}`} icon={GraduationCap} testid="perf-areas-chart">
    {!d.areas.length ? <Empty /> : (
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={d.areas} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} interval={0} tickFormatter={(v) => (narrow ? SHORT[v] || v : v)} />
          <YAxis domain={[0, 10]} tick={{ fontSize: 12, fill: "#64748b" }} />
          <ReferenceLine y={5} stroke="#dc2626" strokeDasharray="4 4" />
          <Tooltip contentStyle={TIP} formatter={(v, n, p) => [`${fmt(v)} · ${p.payload.alunos_abaixo} aluno(s) abaixo de 5`, "Média"]} />
          <Bar dataKey="media" radius={[8, 8, 0, 0]}>{d.areas.map((a) => <Cell key={a.area} fill={tone(a.media)} />)}</Bar>
        </BarChart>
      </ResponsiveContainer>
    )}
  </ChartCard>
  );
};

const PlanButton = ({ d, s }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/dashboard/support-plan", { class_id: d.class_id, student_id: s.student_id, term_order: d.term_order });
      await waitStatus(data.id, ["PRONTO"]);
      toast.success("Plano gerado. Revise, acrescente conteúdos e imprima.");
      navigate(`/reunioes/${data.id}`);
    } catch (e) { toast.error(e.response ? formatApiError(e.response.data?.detail) : e.message); setBusy(false); }
  };
  return (
    <Button onClick={go} disabled={busy} className="mt-3 w-full bg-blue-700 hover:bg-blue-800 sm:w-auto" data-testid="perf-support-plan-btn">
      {busy ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Gerando plano com IA...</> : <><Wand2 className="mr-1.5 h-4 w-4" />Gerar plano de reforço e incentivo</>}
    </Button>
  );
};

const StudentCard = ({ d }) => {
  const narrow = useNarrow();
  const [sid, setSid] = useState("");
  useEffect(() => { setSid(d.students[0]?.student_id || ""); }, [d]);
  const s = d.students.find((x) => x.student_id === sid);
  const data = d.areas.map((a) => ({ label: a.label, aluno: s?.areas[a.area] ?? null, turma: a.media }));
  const L = d.area_labels;
  return (
    <ChartCard title="Aluno × turma por área" icon={Sparkles} testid="perf-student-chart"
      right={d.students.length > 0 && (
        <Select value={sid} onValueChange={setSid}>
          <SelectTrigger className="h-9 w-56" data-testid="perf-student-select"><SelectValue placeholder="Aluno" /></SelectTrigger>
          <SelectContent>{d.students.map((x) => <SelectItem key={x.student_id} value={x.student_id}>{x.full_name}</SelectItem>)}</SelectContent>
        </Select>
      )}>
      {!s ? <Empty /> : (
        <>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} interval={0} tickFormatter={(v) => (narrow ? SHORT[v] || v : v)} />
              <YAxis domain={[0, 10]} tick={{ fontSize: 12, fill: "#64748b" }} />
              <ReferenceLine y={5} stroke="#dc2626" strokeDasharray="4 4" />
              <Tooltip contentStyle={TIP} formatter={(v) => fmt(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="aluno" name="Aluno" fill="#1d4ed8" radius={[6, 6, 0, 0]} />
              <Bar dataKey="turma" name="Média da turma" fill="#94a3b8" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-2 grid gap-2 text-sm sm:grid-cols-2" data-testid="perf-student-actions">
            <div className="rounded-lg bg-red-50 p-3 text-red-800"><b className="flex items-center gap-1"><TrendingDown className="h-4 w-4" />Precisa de reforço</b>
              {s.weakest ? `${L[s.weakest]} (${fmt(s.areas[s.weakest])})` : "Nenhuma área abaixo de 7,0"}
              {s.red_subjects.length > 0 && <div className="text-xs">Notas vermelhas: {s.red_subjects.join(", ")}</div>}
            </div>
            <div className="rounded-lg bg-green-50 p-3 text-green-800"><b className="flex items-center gap-1"><Sparkles className="h-4 w-4" />Aptidão — incentivar</b>
              {s.strongest ? `${L[s.strongest]} (${fmt(s.areas[s.strongest])})` : "Nenhuma área com 7,0 ou mais"}
            </div>
          </div>
          <PlanButton d={d} s={s} />
        </>
      )}
    </ChartCard>
  );
};

const RiskCard = ({ d }) => (
  <ChartCard title="Possibilidade de reprovação" icon={AlertTriangle} testid="perf-risk"
    right={<span className="rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700" data-testid="perf-risk-school">{d.risk_school_total} na escola</span>}>
    <p className="mb-2 text-xs text-slate-500">Alunos da turma com nota vermelha (abaixo de 5,0) em 3 ou mais disciplinas no {d.term_order}º trimestre.</p>
    {!d.risk.length ? <Empty text="Nenhum aluno da turma nessa situação." /> : (
      <ul className="max-h-64 divide-y divide-slate-100 overflow-y-auto" data-testid="perf-risk-list">
        {d.risk.map((r) => (
          <li key={r.student_id} className="py-2" data-testid={`perf-risk-${r.student_id}`}>
            <div className="flex items-center justify-between gap-2"><span className="text-sm font-semibold text-slate-800">{r.full_name}</span>
              <span className="rounded bg-red-600 px-1.5 py-0.5 text-xs font-bold text-white">{r.red_count} vermelhas</span></div>
            <div className="text-xs text-slate-500">{r.red_subjects.join(", ")}</div>
          </li>
        ))}
      </ul>
    )}
  </ChartCard>
);

const ApprovalCard = ({ d }) => (
  <ChartCard title={`Aprovados × reprovados por trimestre — Turma ${d.class_name}`} icon={GraduationCap} testid="perf-approval-chart">
    <p className="mb-2 text-xs text-slate-500">Aprovado: 5,0 ou mais em todas as disciplinas (nota final, já com NOA).</p>
    {!d.approval_by_term.some((t) => t.aprovados + t.reprovados) ? <Empty text="Sem notas lançadas nesta turma." /> : (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={d.approval_by_term} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis dataKey="term" tick={{ fontSize: 12, fill: "#64748b" }} />
        <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748b" }} />
        <Tooltip contentStyle={TIP} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="aprovados" name="Aprovados" fill="#16a34a" radius={[6, 6, 0, 0]} />
        <Bar dataKey="reprovados" name="Reprovados" fill="#dc2626" radius={[6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
    )}
  </ChartCard>
);

const NoaCard = ({ d }) => (
  <ChartCard title={`Alunos com NOA × sem NOA por turma — ${d.term_order}º tri`} icon={AlertTriangle} testid="perf-noa-chart">
    <p className="mb-2 text-xs text-slate-500">Com NOA: ficou abaixo de 5,0 em pelo menos uma disciplina (NOA lançada ou pendente).</p>
    {!d.noa_by_class.some((c) => c.com_noa + c.sem_noa) ? <Empty /> : (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={d.noa_by_class} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis dataKey="turma" tick={{ fontSize: 12, fill: "#64748b" }} />
        <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748b" }} />
        <Tooltip contentStyle={TIP} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="com_noa" name="Com NOA" stackId="n" fill="#f59e0b" />
        <Bar dataKey="sem_noa" name="Sem NOA" stackId="n" fill="#2563eb" radius={[6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
    )}
  </ChartCard>
);

export const PerformanceCharts = ({ scopeParams, schoolKey }) => {
  const [classId, setClassId] = useState("");
  const [term, setTerm] = useState("");
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    api.get("/dashboard/performance", { params: { ...scopeParams, ...(classId ? { class_id: classId } : {}), ...(term ? { term_order: Number(term) } : {}) } })
      .then((r) => setD(r.data)).catch(() => setD({ classes: [] }));
    // eslint-disable-next-line
  }, [schoolKey, classId, term]);
  return (
    <section className="mt-6" data-testid="performance-section">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold text-slate-900">Desempenho dos alunos</h2>
          <p className="text-sm text-slate-500">Por área de conhecimento (BNCC), para planejar reforço nas dificuldades e incentivar as aptidões.</p>
        </div>
        {d?.classes?.length > 0 && (
          <div className="flex gap-2">
            <Select value={d.class_id} onValueChange={setClassId}>
              <SelectTrigger className="w-36" data-testid="perf-class-select"><SelectValue /></SelectTrigger>
              <SelectContent>{d.classes.map((c) => <SelectItem key={c.id} value={c.id}>Turma {c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={String(d.term_order)} onValueChange={setTerm}>
              <SelectTrigger className="w-36" data-testid="perf-term-select"><SelectValue /></SelectTrigger>
              <SelectContent>{[1, 2, 3].map((t) => <SelectItem key={t} value={String(t)}>{t}º Trimestre</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      </div>
      {d === null ? <div className="grid h-40 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-blue-700" /></div>
        : !d.classes?.length ? <Card className="p-4"><Empty text="Nenhuma turma ativa." /></Card> : (
          <div className="grid gap-4 lg:grid-cols-2">
            <AreaChartCard d={d} />
            <StudentCard d={d} />
            <RiskCard d={d} />
            <ApprovalCard d={d} />
            <div className="lg:col-span-2"><NoaCard d={d} /></div>
          </div>
        )}
    </section>
  );
};
